import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import {
  Menu,
  MenuItem,
  Sidebar,
  SubMenu,
  menuClasses,
} from "react-pro-sidebar";
import { Icon } from "../icons";
import { hexToRGBA } from "../../utils/hex-to-rgba";
import "./SidebarStyles.css";

const makePath = (basePath, path = "") =>
  path ? `${basePath}/${path}` : basePath;

const sidebarMenus = [
  {
    label: "홈",
    path: "",
    icon: "homeIcon",
  },
  {
    label: "평가 Wizard",
    path: "assessment/wizard",
    icon: "todoIcon",
  },
  {
    label: "개선대책",
    icon: "addtaskIcon",
    children: [
      {
        label: "개선대책 등록",
        path: "strategy/register",
        icon: "editIcon",
      },
      {
        label: "개선 완료 처리",
        path: "strategy/result",
        icon: "checklistIcon",
      },
    ],
  },
  {
    label: "현장 운영",
    icon: "healthbagIcon",
    children: [
      {
        label: "TBM 실시 내역",
        path: "monitoring/tbm",
        icon: "checklistIcon",
      },
      {
        label: "안전점검 일지",
        path: "monitoring/inspection",
        icon: "viewIcon",
      },
      {
        label: "안전보건교육 일정",
        path: "monitoring/education",
        icon: "calendarIcon",
      },
    ],
  },
  {
    label: "기준정보",
    icon: "settingsIcon",
    children: [
      {
        label: "라인 관리",
        path: "standard/location",
        icon: "nodesIcon",
      },
      {
        label: "공정 관리",
        path: "standard/process",
        icon: "ganttIcon",
      },
      {
        label: "설비 관리",
        path: "standard/machine",
        icon: "repairIcon",
      },
      {
        label: "위험요인 관리",
        path: "standard/cause",
        icon: "warningIcon",
      },
      {
        label: "MSDS 관리",
        path: "standard/msds",
        icon: "folderIcon",
      },
      {
        label: "설비-공정 연결",
        path: "register/machine",
        icon: "repairIcon",
      },
      {
        label: "설비별 위험요인",
        path: "register/cause",
        icon: "warningIcon",
      },
      {
        label: "직원 정보",
        path: "standard/employee",
        icon: "usersIcon",
      },
      {
        label: "위험성 기준표",
        path: "standard/criteriaTable",
        icon: "squareSliderIcon",
      },
      {
        label: "안전보건 기준",
        path: "standard/safetyRegulations",
        icon: "shieldIcon",
      },
      {
        label: "비상 시나리오",
        path: "standard/scenario",
        icon: "sirenIcon",
      },
    ],
  },
  {
    label: "작업자 기능",
    icon: "workerIcon",
    children: [
      {
        label: "유해위험요인 제안",
        path: "worker/hazard-proposal",
        icon: "qrIcon",
      },
      {
        label: "내 조치함",
        path: "worker/action-inbox",
        icon: "addtaskIcon",
      },
      {
        label: "제안 검토/승인",
        path: "worker/hazard-review",
        icon: "checklistIcon",
      },
    ],
  },
  {
    label: "보고서",
    path: "monitoring/integrated-report",
    icon: "chartIcon",
  },
];

const visibleSafeMenuPaths = new Set([
  "",
  "standard/location",
  "standard/process",
  "standard/machine",
  "standard/cause",
  "standard/msds",
  "register/machine",
  "register/cause",
  "assessment/wizard",
  "monitoring/tbm",
  "monitoring/inspection",
  "monitoring/education",
  "worker/hazard-proposal",
  "worker/action-inbox",
  "worker/hazard-review",
  "monitoring/integrated-report",
]);

const visibleSidebarMenus = sidebarMenus
  .map((menu) => {
    if (!menu.children) {
      return visibleSafeMenuPaths.has(menu.path || "") ? menu : null;
    }

    const children = menu.children.filter((item) =>
      visibleSafeMenuPaths.has(item.path)
    );
    return children.length ? { ...menu, children } : null;
  })
  .filter(Boolean);

const SafeSidebar = ({ isMobile = false, toggled = false, onToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { group_code, project_code } = useParams();
  const user = useSelector((state) => state.auth.user);

  const basePath = useMemo(
    () => `/safe/${group_code}/${project_code}`,
    [group_code, project_code]
  );

  const isActive = (path = "") =>
    location.pathname === makePath(basePath, path);

  const isGroupActive = (children = []) =>
    children.some(
      (item) => location.pathname === makePath(basePath, item.path)
    );

  const handleNavigate = (path = "") => {
    navigate(makePath(basePath, path));

    if (isMobile) {
      onToggle?.(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Sidebar
      collapsed={false}
      toggled={toggled}
      onBackdropClick={() => onToggle?.(false)}
      breakPoint="md"
      customBreakPoint="899.95px"
      width="248px"
      rootStyles={{
        height: "100%",
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        zIndex: isMobile ? theme.zIndex.drawer + 2 : theme.zIndex.drawer,
      }}
    >
      {/* <Box
        sx={{
          px: 2,
          py: 2.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 0, lineHeight: 1.2 }}
            >
              Risk Assessment
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              위험성평가
            </Typography>
          </Box>
          <Chip
            size="small"
            label="관리자"
            color="primary"
            variant="outlined"
            sx={{ height: 24, fontWeight: 700 }}
          />
        </Stack>
      </Box> */}

      <Menu
        closeOnClick={isMobile}
        menuItemStyles={{
          root: {
            fontSize: "0.9rem",
            fontWeight: 600,
          },
          button: ({ active, level }) => ({
            height: level === 0 ? 44 : 38,
            margin: level === 0 ? "4px 10px" : "2px 10px 2px 18px",
            paddingLeft: level === 0 ? 12 : 16,
            paddingRight: 12,
            borderRadius: 8,
            color: active
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            backgroundColor: active
              ? hexToRGBA(theme.palette.primary.main, 0.12)
              : "transparent",
            [`&.${menuClasses.active}`]: {
              color: theme.palette.primary.main,
              backgroundColor: hexToRGBA(theme.palette.primary.main, 0.12),
              fontWeight: 800,
            },
            "&:hover": {
              color: theme.palette.primary.main,
              backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08),
            },
          }),
          icon: ({ active }) => ({
            minWidth: 34,
            color: active
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
          }),
          label: {
            whiteSpace: "normal",
            lineHeight: 1.25,
          },
          subMenuContent: {
            backgroundColor: theme.palette.background.paper,
          },
          SubMenuExpandIcon: ({ open }) => ({
            color: open
              ? theme.palette.primary.main
              : theme.palette.text.disabled,
          }),
        }}
      >
        {visibleSidebarMenus.map((menu) =>
          menu.children ? (
            <SubMenu
              key={menu.label}
              label={menu.label}
              icon={<Icon name={menu.icon} width={18} height={18} />}
              defaultOpen={isGroupActive(menu.children)}
              active={isGroupActive(menu.children)}
            >
              {menu.children.map((item) => (
                <MenuItem
                  key={item.path}
                  icon={<Icon name={item.icon} width={16} height={16} />}
                  active={isActive(item.path)}
                  onClick={() => handleNavigate(item.path)}
                >
                  {item.label}
                </MenuItem>
              ))}
            </SubMenu>
          ) : (
            <MenuItem
              key={menu.label}
              icon={<Icon name={menu.icon} width={18} height={18} />}
              active={isActive(menu.path)}
              onClick={() => handleNavigate(menu.path)}
            >
              {menu.label}
            </MenuItem>
          )
        )}
      </Menu>
    </Sidebar>
  );
};

export default SafeSidebar;
